import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { tagStyle, type SearchFilterBarFacet } from "@nexus/ui";

import { REFRESH_MODE_OPTIONS } from "@/lib/dashboard/refreshMode";
import { dashboardRefreshMode, dashboardTags, type DashboardRecord } from "@/lib/dashboard/types";
import { dashboardOwner, type DashboardOwner } from "@/lib/dashboard/dashboardOrigin";

export const TAG_PARAM = "tag";
export const TYPE_PARAM = "type";
export const STATUS_PARAM = "status";
export const KIND_PARAM = "kind";
export const OWNER_PARAM = "owner";

type RefreshMode = "live" | "report";
type DashboardStatus = "draft" | "changes" | "saved";

/** Same names as the status column of the dashboard table. */
const STATUS_OPTIONS: readonly {
  value: DashboardStatus;
  label: string;
  description: string;
  swatchClassName: string;
}[] = [
  {
    value: "draft",
    label: "Draft",
    description: "Never published",
    swatchClassName: "bg-status-warn",
  },
  {
    value: "changes",
    label: "Unpublished changes",
    description: "Published, with edits not published yet",
    swatchClassName: "bg-status-warn",
  },
  { value: "saved", label: "Published", description: "Up to date", swatchClassName: "bg-muted" },
];

export function dashboardStatus(dashboard: DashboardRecord): DashboardStatus {
  return dashboard.state === "draft" ? "draft" : dashboard.draft ? "changes" : "saved";
}

function isStatus(value: string | null): value is DashboardStatus {
  return value === "draft" || value === "changes" || value === "saved";
}

function isRefreshMode(value: string | null): value is RefreshMode {
  return value === "live" || value === "report";
}

function isOwner(value: string | null): value is DashboardOwner {
  return value === "me" || value === "others";
}

const FILTER_PARAMS = [TAG_PARAM, TYPE_PARAM, STATUS_PARAM, OWNER_PARAM];

interface StoredFilters {
  params: string;
  search: string;
}

function readStoredFilters(key: string | undefined): StoredFilters | null {
  if (!key) return null;
  try {
    const raw = window.sessionStorage.getItem(`nexus.dashboard-filters:${key}`);
    const parsed = raw ? (JSON.parse(raw) as Partial<StoredFilters>) : null;
    return parsed && typeof parsed.params === "string" && typeof parsed.search === "string"
      ? {
          params: filterParamsOf(new URLSearchParams(parsed.params)).toString(),
          search: parsed.search,
        }
      : null;
  } catch {
    return null;
  }
}

function writeStoredFilters(key: string | undefined, filters: StoredFilters) {
  if (!key) return;
  try {
    window.sessionStorage.setItem(`nexus.dashboard-filters:${key}`, JSON.stringify(filters));
  } catch {
    // Storage can be unavailable (private mode, quota): filters then simply do not survive navigation.
  }
}

function filterParamsOf(params: URLSearchParams): URLSearchParams {
  const kept = new URLSearchParams();
  for (const name of FILTER_PARAMS)
    for (const value of params.getAll(name)) kept.append(name, value);
  return kept;
}

/**
 * Search and facet filters for a list of dashboards: free text, tags (any of
 * the chosen ones), type (live or report), optional ownership and status.
 * Facets live in the URL (`?tag=a&tag=b&type=report`) so a filtered
 * view can be shared or bookmarked and Back undoes a filter; the free text does
 * not, as it changes with every keystroke.
 */
export function useDashboardTagFilter(
  dashboards: readonly DashboardRecord[] | undefined,
  {
    status: withStatus = false,
    ownership: withOwnership = false,
    persistKey,
  }: {
    status?: boolean;
    ownership?: boolean;
    /**
     * Remembers the filters for this list for the browser session, so opening a
     * dashboard and coming back (by the breadcrumb, the navigation or Back)
     * returns to the same filtered view.
     */
    persistKey?: string;
  } = {},
) {
  const [urlParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!urlParams.has(KIND_PARAM)) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(KIND_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [urlParams, setSearchParams]);
  const [stored] = useState(() => readStoredFilters(persistKey));
  const [search, setSearch] = useState(() => stored?.search ?? "");
  // Arriving without filters in the address restores the ones this list had last.
  const restoring = useRef(
    stored !== null && stored.params !== "" && filterParamsOf(urlParams).toString() === "",
  );
  const searchParams = restoring.current ? new URLSearchParams(stored?.params ?? "") : urlParams;
  useEffect(() => {
    if (!restoring.current || !stored) return;
    restoring.current = false;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [name, value] of new URLSearchParams(stored.params)) next.append(name, value);
        return next;
      },
      { replace: true },
    );
  }, [stored, setSearchParams]);
  const filterParams = filterParamsOf(searchParams).toString();
  useEffect(() => {
    if (!restoring.current) writeStoredFilters(persistKey, { params: filterParams, search });
  }, [persistKey, filterParams, search]);
  const all = useMemo(() => dashboards ?? [], [dashboards]);

  const tags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const dashboard of all) {
      for (const tag of new Set(dashboardTags(dashboard)))
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
    return [...counts]
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
  }, [all]);

  // A tag nobody uses any more (stale link) is ignored rather than showing an empty list.
  const requestedTags = searchParams.getAll(TAG_PARAM);
  const selectedTags = useMemo(
    () =>
      requestedTags.filter(
        (tag, index) => requestedTags.indexOf(tag) === index && tags.some((t) => t.tag === tag),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestedTags.join("\u0000"), tags],
  );
  const requestedType = searchParams.get(TYPE_PARAM);
  const selectedType = isRefreshMode(requestedType) ? requestedType : null;
  const requestedStatus = searchParams.get(STATUS_PARAM);
  const selectedStatus = withStatus && isStatus(requestedStatus) ? requestedStatus : null;
  const requestedOwner = searchParams.get(OWNER_PARAM);
  const selectedOwner = withOwnership && isOwner(requestedOwner) ? requestedOwner : null;

  const writeParams = useCallback(
    (update: (next: URLSearchParams) => void) =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          update(next);
          return next;
        },
        { replace: false },
      ),
    [setSearchParams],
  );
  const selectTags = useCallback(
    (next: readonly string[]) =>
      writeParams((params) => {
        params.delete(TAG_PARAM);
        for (const tag of next) params.append(TAG_PARAM, tag);
      }),
    [writeParams],
  );
  const selectType = useCallback(
    (type: RefreshMode | null) =>
      writeParams((params) => {
        if (type) params.set(TYPE_PARAM, type);
        else params.delete(TYPE_PARAM);
      }),
    [writeParams],
  );

  const selectStatus = useCallback(
    (status: DashboardStatus | null) =>
      writeParams((params) => {
        if (status) params.set(STATUS_PARAM, status);
        else params.delete(STATUS_PARAM);
      }),
    [writeParams],
  );

  const selectOwner = useCallback(
    (owner: DashboardOwner | null) =>
      writeParams((params) => {
        if (owner) params.set(OWNER_PARAM, owner);
        else params.delete(OWNER_PARAM);
      }),
    [writeParams],
  );

  const ownerCounts = useMemo(() => {
    const counts: Record<DashboardOwner, number> = { me: 0, others: 0 };
    for (const dashboard of all) counts[dashboardOwner(dashboard)] += 1;
    return counts;
  }, [all]);

  const statusCounts = useMemo(() => {
    const counts: Record<DashboardStatus, number> = { draft: 0, changes: 0, saved: 0 };
    for (const dashboard of all) counts[dashboardStatus(dashboard)] += 1;
    return counts;
  }, [all]);

  const typeCounts = useMemo(() => {
    const counts: Record<RefreshMode, number> = { live: 0, report: 0 };
    for (const dashboard of all) counts[dashboardRefreshMode(dashboard)] += 1;
    return counts;
  }, [all]);

  const query = search.trim().toLocaleLowerCase();
  const matchesOtherFilters = useCallback(
    (dashboard: DashboardRecord) => {
      const own = dashboardTags(dashboard);
      if (selectedTags.length > 0 && !selectedTags.some((tag) => own.includes(tag))) return false;
      if (selectedType && dashboardRefreshMode(dashboard) !== selectedType) return false;
      if (selectedStatus && dashboardStatus(dashboard) !== selectedStatus) return false;
      if (selectedOwner && dashboardOwner(dashboard) !== selectedOwner) return false;
      if (!query) return true;
      return [
        dashboard.draft?.name ?? dashboard.name,
        dashboard.draft?.description ?? dashboard.description,
        ...own,
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    },
    [selectedTags, selectedType, selectedStatus, selectedOwner, query],
  );
  const filtered = useMemo(() => all.filter(matchesOtherFilters), [all, matchesOtherFilters]);

  const facets = useMemo<SearchFilterBarFacet[]>(() => {
    const list: SearchFilterBarFacet[] = [];
    if (withOwnership) {
      list.push({
        id: "owner",
        label: "Ownership",
        multiple: false,
        options: [
          { value: "me", label: "Owned by me", count: ownerCounts.me },
          { value: "others", label: "Shared with me", count: ownerCounts.others },
        ],
        selected: selectedOwner ? [selectedOwner] : [],
        onChange: (next) =>
          selectOwner(isOwner(next[0] ?? null) ? (next[0] as DashboardOwner) : null),
      });
    }
    if (withStatus) {
      list.push({
        id: "status",
        label: "State",
        multiple: false,
        options: STATUS_OPTIONS.filter((option) => statusCounts[option.value] > 0).map(
          (option) => ({
            ...option,
            count: statusCounts[option.value],
          }),
        ),
        selected: selectedStatus ? [selectedStatus] : [],
        onChange: (next) =>
          selectStatus(isStatus(next[0] ?? null) ? (next[0] as DashboardStatus) : null),
      });
    }
    list.push({
      id: "type",
      label: "Type",
      multiple: false,
      options: REFRESH_MODE_OPTIONS.filter((option) => typeCounts[option.value] > 0).map(
        (option) => ({
          value: option.value,
          label: option.label,
          description: option.description,
          icon: option.icon,
          count: typeCounts[option.value],
        }),
      ),
      selected: selectedType ? [selectedType] : [],
      onChange: (next) =>
        selectType(isRefreshMode(next[0] ?? null) ? (next[0] as RefreshMode) : null),
    });
    if (tags.length > 0) {
      list.push({
        id: "tag",
        label: "Tags",
        display: "chips",
        options: tags.map(({ tag, count }) => ({
          value: tag,
          label: tag,
          count,
          swatchClassName: "tag-chip",
          swatchStyle: tagStyle(tag),
        })),
        selected: selectedTags,
        onChange: selectTags,
      });
    }
    return list;
  }, [
    withStatus,
    statusCounts,
    selectedStatus,
    selectStatus,
    tags,
    typeCounts,
    selectedTags,
    selectedType,
    selectTags,
    selectType,
    withOwnership,
    ownerCounts,
    selectedOwner,
    selectOwner,
  ]);

  const clear = useCallback(() => {
    setSearch("");
    writeParams((params) => {
      params.delete(TAG_PARAM);
      params.delete(TYPE_PARAM);
      params.delete(STATUS_PARAM);
      params.delete(KIND_PARAM);
      params.delete(OWNER_PARAM);
    });
  }, [writeParams]);

  return {
    facets,
    filtered,
    search,
    setSearch,
    clear,
    selectedTags,
    selectedType,
    selectedStatus,
    selectedOwner,
    /** Whether anything narrows the list: text or a facet. */
    isFiltered:
      query.length > 0 ||
      selectedTags.length > 0 ||
      selectedType !== null ||
      selectedStatus !== null ||
      selectedOwner !== null,
    total: all.length,
  };
}
